<template>
  <div
    class="crypkit-base-input"
    :class="{
      'validation-error': !!errorMessage,
      disabled: disabled,
      [size]: size,
      'crypkit-base-input-password': type === 'password',
    }"
  >
    <Label
      v-if="label !== ''"
      :label="label"
      :required="required"
      :hide-asterisk="hideAsterisk"
      :help="help"
      :for="id"
    >
      <template v-if="$slots['label-append']" #append>
        <slot name="label-append" />
      </template>
    </Label>

    <div
      v-click-outside="handleClickOutside"
      class="crypkit-base-input-container"
      :class="{
        'bulky-border': bulkyBorder,
      }"
    >
      <div v-if="$slots['prepend']" class="crypkit-base-input-prepend">
        <slot name="prepend" />
      </div>

      <input
        :id="id"
        ref="input"
        v-model="inputValue"
        :required="required"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :maxlength="maxlength"
        :placeholder="placeholder"
        :name="name"
        :type="inputType"
        :min="min ?? undefined"
        :max="max ?? undefined"
        v-bind="passwordManagerAttrs"
        @input="handleChange"
        @paste="handlePaste"
        @keyup.enter="enterPressed"
        @keyup.esc="autocompleteCollapsed = false"
        @focus="_handleFocus"
        @blur="_handleBlur"
      />

      <Spinner v-if="loading" class="fill-brand" />

      <div
        v-if="type === 'password'"
        class="crypkit-base-input-eye"
        @click.stop="showPassword = !showPassword"
      >
        <SvgIcon v-if="inputType === 'password'" icon="Show" />
        <SvgIcon v-else icon="Hide" />
      </div>

      <div v-if="type === 'number'" class="crypkit-base-input-arrows">
        <div @click="makeStep('up')">
          <SvgIcon icon="ArrowUp" />
        </div>
        <div @click="makeStep('down')">
          <SvgIcon icon="ArrowDown" />
        </div>
      </div>

      <div v-if="$slots['append']" class="crypkit-base-input-append">
        <slot name="append" />
      </div>

      <!-- Input confirm message -->
      <div v-if="confirmMessage && showConfirm" class="confirm-messages">
        {{ confirmMessage }}
        <div class="flex justify-end">
          <button class="mr-2" @click.stop="cancel">Cancel</button>
          <button @click.stop="showConfirm = false">Ok</button>
        </div>
      </div>

      <SelectItems
        v-if="autocompleteItemsFiltered.length && autocompleteCollapsed"
        v-model:collapsed="autocompleteCollapsed"
        :options="autocompleteItemsFiltered"
        :group-values="autocompleteGroupValues"
        :position-to="$refs.input as HTMLInputElement"
        @select="selectAutocompleteItem"
      />
    </div>
    <!-- Input hint message -->
    <div v-if="hint" class="hint-messages">{{ hint }}</div>
    <!-- Input validation message -->
    <div v-if="!hideErrors" class="validation-messages">{{ errorMessage }}</div>
  </div>
</template>

<script lang="ts">
import './BasicInput.scss'

import { type PropType, defineComponent, toRef } from 'vue'
import BigNumber from 'bignumber.js'
import { useField } from 'vee-validate'
import { v4 as uuidv4 } from 'uuid'

import Spinner from '@/assets/loaders/Spinner.svg?component'
import ClickOutside from '@/components/directives/click-outside'
import Label from '@/components/misc/Label'
import SvgIcon from '@/components/misc/SvgIcon'
import SelectItems from '@/components/controls/SelectItems'
import { useNumberParser } from '@/composables/number_parser'
import fuzzySearch from '@/helpers/fuzzySearch'

export default defineComponent({
  components: {
    Label,
    SelectItems,
    Spinner,
    SvgIcon,
  },

  directives: {
    ClickOutside,
  },

  props: {
    type: {
      type: String,
      default: 'text',
      required: false,
    },
    size: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: () => uuidv4(),
    },
    modelValue: {
      type: [String, Number] as PropType<string | number | null>,
      default: '',
    },
    emptyAsZero: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    hideAsterisk: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: null,
    },
    hideErrors: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: '',
    },
    autocomplete: {
      type: String as PropType<string | undefined>,
      default: undefined,
    },
    maxlength: {
      type: String as PropType<string | undefined>,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: '',
    },
    step: {
      type: [String, Number],
      default: 1,
    },
    min: {
      type: Number as PropType<number | null>,
      default: null,
    },
    max: {
      type: Number as PropType<number | null>,
      default: null,
    },
    confirmMessage: {
      type: String,
      default: '',
    },
    help: {
      type: String,
      default: null,
    },
    autocompleteItems: {
      type: Array,
      default: (): any[] => [],
    },
    autocompleteItemKey: {
      type: String,
      default: null,
    },
    autocompleteMinChars: {
      type: Number,
      default: 3,
    },
    autocompleteGroupValues: {
      type: String,
      default: null,
    },
    autocompleteSelectedKey: {
      type: String,
      default: 'text',
    },
    bulkyBorder: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    ignorePasswordManagers: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['submit', 'change', 'autocomplete-selected', 'update:modelValue'],

  setup(props, { emit }) {
    const name = toRef(props, 'name')
    const initialValue = toRef(props, 'modelValue')

    const {
      value: inputValue,
      errorMessage,
      handleBlur,
    } = useField(name, undefined, {
      initialValue,
      validateOnValueUpdate: false,
    })

    const handleChange = () => {
      emit('update:modelValue', inputValue.value)
    }

    const { parse: parseNumber } = useNumberParser()

    return {
      inputValue,
      errorMessage,
      handleBlur,
      handleChange,
      parseNumber,
    }
  },

  data() {
    return {
      showConfirm: false,
      showPassword: false,
      autocompleteCollapsed: false,
      blurTimeout: null as number | null,
    }
  },

  computed: {
    passwordManagerAttrs(): Record<string, string> {
      if (!this.ignorePasswordManagers) return {}
      return {
        'data-1p-ignore': '', // 1Password
        'data-lpignore': 'true', // LastPass
        'data-form-type': 'other', // Dashlane
      }
    },
    inputType(): string {
      const { type, showPassword } = this
      if (type != 'password') {
        return type
      }
      return showPassword ? 'text' : 'password'
    },
    autocompleteItemsFiltered(): any[] {
      const {
        autocompleteItems,
        autocompleteMinChars,
        autocompleteGroupValues,
        inputValue,
      } = this
      if (
        autocompleteItems.length === 0 ||
        String(inputValue).length < autocompleteMinChars
      ) {
        return []
      }

      let items: any[] = autocompleteItems
      if (autocompleteGroupValues) {
        const filteredGroups: any[] = []
        items.forEach((group: any) => {
          const groupOptions = fuzzySearch(
            String(inputValue),
            group[autocompleteGroupValues]
          )
          if (groupOptions.length) {
            const filteredGroup = {
              ...group,
              [autocompleteGroupValues]: groupOptions,
            }
            filteredGroups.push(filteredGroup)
          }
        })
        return filteredGroups
      }
      items = this.mapAutocompleteItems(items)
      return fuzzySearch(String(inputValue), items)
    },
  },

  watch: {
    inputValue(value: string | number) {
      if (value) {
        if (this.confirmMessage) {
          this.showConfirm = true
        }
        if (this.autocompleteItemsFiltered.length) {
          this.autocompleteCollapsed = true
        }
      }
    },
    modelValue(value) {
      if (this.inputValue !== value) {
        this.inputValue = value
      }
    },
  },

  methods: {
    enterPressed() {
      this.$emit('submit')
    },
    checkForZero(direction: string): void {
      if (!this.emptyAsZero || this.type != 'number') return
      if (direction == 'focus') {
        if (
          this.inputValue !== '' &&
          new BigNumber(this.inputValue ?? 0).isZero()
        ) {
          this.$emit('update:modelValue', null)
        }
      } else if (this.inputValue == null || this.inputValue == '') {
        this.$emit('update:modelValue', 0)
      }
    },
    blur() {
      // This method allows validation providers to work
      // with modes like "lazy" or "eager" (which listen on change event)
      this.$emit('change', this.inputValue)
    },
    handlePaste(event: ClipboardEvent) {
      if (this.type != 'number') {
        return
      }

      event.preventDefault()
      const clipboardData = event.clipboardData?.getData('text/plain')
      if (clipboardData) {
        const parsedValue = this.parseNumber(clipboardData)
        if (parsedValue !== null) {
          this.$emit('update:modelValue', parsedValue)
        }
      }
    },
    cancel() {
      this.inputValue = ''
      this.showConfirm = false
    },
    handleClickOutside() {
      if (this.showConfirm) {
        this.cancel()
      }
      this.autocompleteCollapsed = false
    },
    _handleFocus() {
      this.checkForZero('focus')
      // Cancel pending blur timeout to prevent it from closing
      // the autocomplete we're about to reopen
      if (this.blurTimeout) {
        clearTimeout(this.blurTimeout)
        this.blurTimeout = null
      }
      if (this.autocompleteItemsFiltered.length) {
        this.autocompleteCollapsed = true
      }
    },
    _handleBlur() {
      this.checkForZero('blur')
      // Delay closing autocomplete to allow click/touch events to fire
      if (this.blurTimeout) {
        clearTimeout(this.blurTimeout)
      }

      this.blurTimeout = window.setTimeout(() => {
        this.autocompleteCollapsed = false
        this.blurTimeout = null
      }, 200)

      this.handleBlur()
    },
    mapAutocompleteItems(items: any[]): any[] {
      const { autocompleteItemKey } = this
      if (typeof items[0] === 'string') {
        items = items.map((item) => ({ text: item }))
      } else if (autocompleteItemKey) {
        items = items.map((item) => ({
          ...item,
          text: item[autocompleteItemKey],
        }))
      }
      return items
    },
    selectAutocompleteItem(item: any) {
      const { autocompleteGroupValues, autocompleteSelectedKey } = this
      if (
        item &&
        autocompleteGroupValues &&
        Array.isArray(item[autocompleteGroupValues])
      ) {
        // do not select group header
        return
      }

      // Clear blur timeout to prevent race condition
      if (this.blurTimeout) {
        clearTimeout(this.blurTimeout)
        this.blurTimeout = null
      }

      this.$emit('update:modelValue', item[autocompleteSelectedKey])
      this.$emit('autocomplete-selected', item)
      this.$nextTick(() => {
        this.autocompleteCollapsed = false
      })
    },
    makeStep(direction: 'up' | 'down') {
      const step =
        typeof this.step === 'string' ? parseFloat(this.step) : this.step
      let value = new BigNumber(this.inputValue || 0)

      if (direction == 'up') {
        value = value.plus(step)
      } else {
        value = value.minus(step)
      }

      if (this.max !== null && value.isGreaterThan(this.max)) {
        return
      }
      if (this.min !== null && value.isLessThan(this.min)) {
        return
      }

      this.$emit('update:modelValue', value.toNumber())
    },
  },
})
</script>
